import type { NetClient } from './NetClient';
import type {
  BroadcastSnakeState, SnakeGone, ClientSnakeState,
  ClientInput, SnakeDied, SnakeAte,
} from './messages';
import {
  applyServerStatePatch,
  clearServerWorld,
  setLocalSessionId,
} from './serverWorld';
import { useGameStore } from '../store/gameStore';

/**
 * Module-level owner of the player's active multiplayer NetClient.
 *
 * Why this exists — production-critical:
 *
 *   MatchmakingGate (and RoomBrowserModal) create a NetClient, await a
 *   successful join, then call onResolved('live') which unmounts the
 *   gate. Their useEffect cleanup naively called `client.leave()`,
 *   which sent a code-4000 "consented" disconnect to the server within
 *   milliseconds of the successful join. The player perceived themselves
 *   as "in-game" but their WebSocket was severed; the server's roster
 *   showed 0 humans even when several real players had just clicked PLAY
 *   NOW. The HUD's net* fields were stale because the snapshot listener
 *   died with the gate.
 *
 *   This module:
 *     1. Adopts the NetClient when a join resolves to 'live'
 *     2. Re-subscribes to snapshot + status, pushing into the store
 *     3. Owns the lifecycle: the client only leaves when the player
 *        explicitly returns Home or a new join replaces it
 *
 *   The store stays fresh on every server tick (snapshot listener lives
 *   here, not in React), the connection survives view transitions, and
 *   the matchmaker's `metadata.humans` accurately reflects active
 *   players.
 *
 * Single-session by design: only one live multiplayer session per
 * browser tab. Adopting a new client tears down the previous one.
 */

let currentClient: NetClient | null = null;
let offSnapshot: (() => void) | null = null;
let offStatus: (() => void) | null = null;
let offSnakeState: (() => void) | null = null;
let offSnakeGone: (() => void) | null = null;
let offSnakeDied: (() => void) | null = null;
let offSnakeAte: (() => void) | null = null;
let offMilestone: (() => void) | null = null;
let offSurvival: (() => void) | null = null;

// ── Phase-2-Full event sink ────────────────────────────────────────

/** Callbacks registered by the engine for visual reactions to server
 *  events. Kept in this module so subscribers don't have to know about
 *  the NetClient layer. */
export interface SnakeDiedListener { (e: SnakeDied): void }
const snakeDiedListeners = new Set<SnakeDiedListener>();
export function onSnakeDied(fn: SnakeDiedListener): () => void {
  snakeDiedListeners.add(fn);
  return () => snakeDiedListeners.delete(fn);
}

/** Per-eat callbacks — drive chomp animation, digesting orbs, audio,
 *  and the floating "+N" toast on the local player's eats. */
export interface SnakeAteListener { (e: SnakeAte): void }
const snakeAteListeners = new Set<SnakeAteListener>();
export function onSnakeAte(fn: SnakeAteListener): () => void {
  snakeAteListeners.add(fn);
  return () => snakeAteListeners.delete(fn);
}

/** Mass-milestone events from the server. Fires exactly once per
 *  milestone per life — the HUD shows a celebration toast +
 *  particles in response. */
export interface MilestoneEvent { mass: number }
export interface MilestoneListener { (e: MilestoneEvent): void }
const milestoneListeners = new Set<MilestoneListener>();
export function onMilestone(fn: MilestoneListener): () => void {
  milestoneListeners.add(fn);
  return () => milestoneListeners.delete(fn);
}

/** Survival-time milestones (60s / 2min / 5min alive). Server fires
 *  exactly once per threshold per life; HUD shows a celebratory toast
 *  framed as "you survived X" — the data anchor identified at the
 *  30s-1min retention cliff. */
export interface SurvivalEvent { ms: number }
export interface SurvivalListener { (e: SurvivalEvent): void }
const survivalListeners = new Set<SurvivalListener>();
export function onSurvival(fn: SurvivalListener): () => void {
  survivalListeners.add(fn);
  return () => survivalListeners.delete(fn);
}

/** Type-checked input send. No-op when no live client — the engine
 *  can call unconditionally. */
export function sendInput(payload: ClientInput): void {
  currentClient?.send('input', payload);
}

/** Request a respawn after death. Server resets the snake to spawn
 *  state. No-op when not connected (training mode handles respawn
 *  locally via World.respawnPlayer). */
export function sendRespawn(): void {
  currentClient?.send('respawn', {});
}

// ── Remote snake registry ──────────────────────────────────────────

/**
 * Per-remote-snake live state, populated by `snake_state` relays
 * coming from the server. The renderer reads this each frame to
 * draw remote players alongside the local simulation.
 *
 * Two snapshots are kept (`prev` + `curr`) so the renderer can
 * interpolate position between updates — at 10 Hz the raw stream
 * would look like a snake teleporting every 100 ms; interpolation
 * smooths it into continuous motion. Step 3 wires the rendering.
 *
 * `receivedAt` is wall-clock ms when we got the update; the renderer
 * uses (now - receivedAt) divided by the inter-update interval to
 * pick an interpolation alpha.
 */
export interface RemoteSnake {
  /** Sender's sessionId — matches the roster entry. */
  id: string;
  // Current snapshot
  hx: number;
  hy: number;
  heading: number;
  mass: number;
  /** Sparse body samples as [x0,y0,...] (unflattened lazily). */
  segments: number[];
  boosting: boolean;
  receivedAt: number;
  // Previous snapshot — used by the renderer for inter-update
  // interpolation. `prevReceivedAt === 0` means we've only seen
  // one update so far and should render at the current snapshot
  // without interpolation.
  prevHx: number;
  prevHy: number;
  prevHeading: number;
  prevMass: number;
  prevSegments: number[];
  prevReceivedAt: number;
}

/** Source of truth for the renderer. Direct map access — every
 *  frame's renderer call does `for (const r of remoteSnakes.values())`
 *  which is the cheapest possible read path. */
export const remoteSnakes = new Map<string, RemoteSnake>();

/** Wall-clock ms after which a remote snake with no fresh update is
 *  reaped from the map. 2500 = 25× the 100 ms send interval, so a
 *  client losing 10+ updates in a row counts as gone. The TTL
 *  catches drops that didn't get an explicit `snake_gone` (e.g. a
 *  client that crashed without a clean leave). */
const REMOTE_TTL_MS = 2500;
/** TTL sweep cadence. 1 s = ~10× the send interval; small map, cheap walk. */
const REMOTE_TTL_SWEEP_MS = 1000;
let remoteTtlTimer: number | null = null;

function startTtlSweep(): void {
  if (remoteTtlTimer !== null) return;
  remoteTtlTimer = window.setInterval(() => {
    const now = Date.now();
    for (const [id, snake] of remoteSnakes) {
      if (now - snake.receivedAt > REMOTE_TTL_MS) remoteSnakes.delete(id);
    }
  }, REMOTE_TTL_SWEEP_MS);
}
function stopTtlSweep(): void {
  if (remoteTtlTimer !== null) {
    clearInterval(remoteTtlTimer);
    remoteTtlTimer = null;
  }
}

/** Apply an incoming snake_state to the registry. Rotates the
 *  current snapshot into `prev` so the renderer always has two
 *  reference points to interpolate between. */
function applySnakeState(p: BroadcastSnakeState): void {
  const now = Date.now();
  const existing = remoteSnakes.get(p.id);
  if (existing) {
    // Rotate: current → previous; new payload becomes current.
    existing.prevHx        = existing.hx;
    existing.prevHy        = existing.hy;
    existing.prevHeading   = existing.heading;
    existing.prevMass      = existing.mass;
    existing.prevSegments  = existing.segments;
    existing.prevReceivedAt = existing.receivedAt;
    existing.hx       = p.hx;
    existing.hy       = p.hy;
    existing.heading  = p.h;
    existing.mass     = p.m;
    existing.segments = p.s;
    existing.boosting = p.b === 1;
    existing.receivedAt = now;
  } else {
    // First update for this sender — `prev` initialized empty
    // (renderer treats prevReceivedAt = 0 as "skip interpolation").
    remoteSnakes.set(p.id, {
      id: p.id,
      hx: p.hx, hy: p.hy, heading: p.h, mass: p.m,
      segments: p.s, boosting: p.b === 1, receivedAt: now,
      prevHx: 0, prevHy: 0, prevHeading: 0, prevMass: p.m, prevSegments: [],
      prevReceivedAt: 0,
    });
  }
}

/** Send a typed message to the server through the active live client.
 *  No-op when there's no live session, so the GameEngine's 10 Hz
 *  sender can run unconditionally without if-guards. */
export function sendLive<T>(type: string, payload: T): void {
  currentClient?.send(type, payload);
}

/** Convenience wrapper: type-checked snake_state send. */
export function sendSnakeState(payload: ClientSnakeState): void {
  currentClient?.send('snake_state', payload);
}

// ── Roster cache for remote-snake rendering ────────────────────────

/**
 * Per-player identity cached from the latest ArenaSnapshot. The
 * Phase-2-Lite renderer needs to look up a remote sender's hue/name/
 * pattern to draw their snake correctly, but the `snake_state` relay
 * deliberately excludes that info (it's slow-changing, doesn't belong
 * in the 10 Hz hot path). This cache is the bridge: every ArenaSnapshot
 * we receive populates the map; remote-renderer reads from it per
 * frame.
 *
 * Indexed by Colyseus sessionId — matches RemoteSnake.id.
 */
export interface RosterEntry {
  hue: number;
  name: string;
  pattern: string;
}
const roster = new Map<string, RosterEntry>();

export function getRosterEntry(id: string): RosterEntry | undefined {
  return roster.get(id);
}

/**
 * Take ownership of a connected NetClient. Wires its snapshot + status
 * streams into the global store and tears down any prior adoption.
 *
 * The handed-off client should already be connected (its join promise
 * has resolved). Adopting a non-connected client is allowed but the
 * store will reflect whatever status the client subsequently reports.
 */
export function adoptLiveClient(client: NetClient): void {
  if (currentClient === client) return; // re-entrant guard

  // Tear down any previous adoption — leaves the old room cleanly so
  // the server can free its seat instead of waiting on the 12 s
  // reconnect timeout.
  if (currentClient) {
    if (offSnapshot)   { offSnapshot();   offSnapshot   = null; }
    if (offStatus)     { offStatus();     offStatus     = null; }
    if (offSnakeState) { offSnakeState(); offSnakeState = null; }
    if (offSnakeGone)  { offSnakeGone();  offSnakeGone  = null; }
    if (offSnakeDied)  { offSnakeDied();  offSnakeDied  = null; }
    if (offSnakeAte)   { offSnakeAte();   offSnakeAte   = null; }
    if (offMilestone)  { offMilestone();  offMilestone  = null; }
    if (offSurvival)   { offSurvival();   offSurvival   = null; }
    remoteSnakes.clear();
    roster.clear();
    clearServerWorld();
    void currentClient.leave();
  }

  currentClient = client;

  offSnapshot = client.onSnapshot((snap) => {
    // Roster cache for the Phase-2-Lite renderer. Rebuilt on every
    // snapshot — drop the previous map's stale entries (a player
    // who left) and replace with the current truth. Cheap: typical
    // roster is <30 entries, snapshots fire ~20 Hz.
    roster.clear();
    let humans = 0;
    snap.players.forEach((p) => {
      const isBot = (p as unknown as { isBot?: boolean }).isBot === true;
      if (!isBot) humans++;
      roster.set(p.sessionId, {
        hue: p.hue,
        name: p.name,
        pattern: p.pattern,
      });
    });
    const store = useGameStore.getState();
    store.setNetRoomCode(snap.roomCode || null);
    store.setNetArenaName(snap.arenaName || null);
    store.setNetPlayers(humans);
  });

  offStatus = client.onStatus((s) => {
    useGameStore.getState().setNetStatus(s);
  });

  // Phase-2-Lite relay subscriptions. After Session 4 the engine no
  // longer SENDS snake_state (it sends `input` instead), but the
  // RECEIVE handler stays wired so a future "lite events only" code
  // path could resurrect the relay cheaply. With no sender on either
  // side, these handlers idle in production.
  offSnakeState = client.onMessage<BroadcastSnakeState>('snake_state', applySnakeState);
  offSnakeGone  = client.onMessage<SnakeGone>('snake_gone', (p) => {
    remoteSnakes.delete(p.id);
  });

  // Phase-2-Full: state-driven world rendering. Every Colyseus patch
  // (≈ 20 Hz) mirrors the authoritative state into serverWorld for the
  // engine's render path. The local session id is set once so render
  // helpers can locate the player's own server-side entry.
  setLocalSessionId(client.sessionId);
  const room = client.activeRoom;
  if (room) {
    const stateListener = (state: unknown): void => {
      applyServerStatePatch(state);
    };
    room.onStateChange(stateListener);
    // colyseus.js v0.16 onStateChange returns void; the listener
    // is auto-detached when the room is left, which we already
    // sequence in disposeLiveClient. No need to track an off().
  }

  // Phase-2-Full: snake_died fan-out. Engine subscribes via
  // onSnakeDied(); we just rebroadcast to local listeners.
  offSnakeDied = client.onMessage<SnakeDied>('snake_died', (p) => {
    for (const fn of snakeDiedListeners) fn(p);
  });
  offMilestone = client.onMessage<MilestoneEvent>('milestone', (p) => {
    for (const fn of milestoneListeners) fn(p);
  });
  offSurvival = client.onMessage<SurvivalEvent>('survival', (p) => {
    for (const fn of survivalListeners) fn(p);
  });
  offSnakeAte = client.onMessage<SnakeAte>('snake_ate', (p) => {
    for (const fn of snakeAteListeners) fn(p);
  });

  // Start the TTL sweeper so dropped clients without an explicit
  // snake_gone eventually clear out (e.g. browser crash, network drop
  // that never recovered).
  startTtlSweep();
}

/**
 * End the current live multiplayer session cleanly. Called when the
 * player exits to home / Training mode, OR when a new join replaces
 * the current adoption.
 *
 * Resets the store's net* fields so any stale connected-state
 * indicators (room code chip, "X live" badge) clear immediately.
 */
export function disposeLiveClient(): void {
  if (!currentClient) {
    // Still reset store so a stale connect chip doesn't linger after
    // a navigation that bypassed the adopt path.
    const store = useGameStore.getState();
    store.setNetStatus('idle');
    store.setNetRoomCode(null);
    store.setNetArenaName(null);
    store.setNetPlayers(0);
    return;
  }
  if (offSnapshot)   { offSnapshot();   offSnapshot   = null; }
  if (offStatus)     { offStatus();     offStatus     = null; }
  if (offSnakeState) { offSnakeState(); offSnakeState = null; }
  if (offSnakeGone)  { offSnakeGone();  offSnakeGone  = null; }
  if (offSnakeDied)  { offSnakeDied();  offSnakeDied  = null; }
  remoteSnakes.clear();
  roster.clear();
  clearServerWorld();
  stopTtlSweep();
  const c = currentClient;
  currentClient = null;
  void c.leave();
  const store = useGameStore.getState();
  store.setNetStatus('idle');
  store.setNetRoomCode(null);
  store.setNetArenaName(null);
  store.setNetPlayers(0);
}

/** Read-only handle for components that need direct access (rare —
 *  most should go through the store). */
export function getLiveClient(): NetClient | null {
  return currentClient;
}

/** True when a live NetClient is currently owned by this module. */
export function hasLiveClient(): boolean {
  return currentClient !== null;
}
